import { repositoryIds, removeRepository, addRepository } from '../repositories/RepositoryManager.ts';
/*
 * Yomiri Plugin Manager & Dynamic Section Engine
 * Orchestrates plugin installation, updates, catalog querying and dynamic UI sections.
 */

import { useState, useEffect } from 'react';
import { PluginStore, EVENT_PLUGINS_CHANGED } from './PluginStore.ts';
import { PluginValidator } from './PluginValidator.ts';
import { PluginDispatcher } from './PluginDispatcher.ts';
import { COMMUNITY_PLUGINS } from './PluginCommunityCatalog.ts';
import type {
  InstalledPlugin,
  DynamicSection,
  PluginCatalogResponse,
  PluginMetaResponse,
  PluginStreamResponse,
  PluginReaderResponse,
} from './PluginTypes.ts';

export class PluginManager {
  private static instance: PluginManager;

  public static getInstance(): PluginManager {
    if (!PluginManager.instance) {
      PluginManager.instance = new PluginManager();
    }
    return PluginManager.instance;
  }

  /**
   * Installs a plugin either from a community preset or a custom remote manifest URL.
   */
  public async installPlugin(urlOrId: string): Promise<{ success: boolean; plugin?: InstalledPlugin; error?: string }> {
    const trimmed = urlOrId.trim();

    // 1. Check if it matches a preset in the Community Directory
    const communityPreset = COMMUNITY_PLUGINS.find(p => p.url === trimmed || p.manifest.id === trimmed);
    if (communityPreset) {
      if (communityPreset.url === '/yomiri-sources.json') {
        try {
          await addRepository('/yomiri-sources.json');
        } catch {
          // ignore if already added
        }
      }
      const installed: InstalledPlugin = {
        url: communityPreset.url,
        manifest: communityPreset.manifest,
        enabled: true,
        installedAt: Date.now(),
        lastUpdated: Date.now(),
        staticPayload: communityPreset.staticPayload,
      };
      PluginStore.save(installed);
      return { success: true, plugin: installed };
    }

    // 2. Validate URL
    if (!PluginValidator.isSafeUrl(trimmed)) {
      return { success: false, error: 'URL inválida. Debe comenzar con https:// o http://' };
    }

    try {
      // 3. Fetch remote manifest
      let rawManifest: any;
      try {
        rawManifest = await PluginDispatcher.fetchManifest(trimmed);
      } catch (e) {
        // Fallback: Check if it is a Tachiyomi or Yomiri repository
        try {
          await addRepository(trimmed);
          const repoPlugin: InstalledPlugin = {
            url: trimmed,
            manifest: {
              id: 'repo.' + trimmed.replace(/[^a-zA-Z0-9]/g, '_'),
              name: 'Repositorio ' + (new URL(trimmed).hostname || 'Externo'),
              version: '1.0.0',
              description: 'Repositorio de fuentes agregado a Yomiri.',
              types: ['manga'],
              resources: ['catalog', 'meta', 'reader'],
              catalogs: [{ type: 'manga', id: 'repo', name: 'Fuentes' }],
            },
            enabled: true,
            installedAt: Date.now(),
            lastUpdated: Date.now(),
          };
          PluginStore.save(repoPlugin);
          return { success: true, plugin: repoPlugin };
        } catch (repoErr: any) {
          throw e;
        }
      }

      // If payload is a repository format, add via addRepository
      if (rawManifest && (rawManifest.format?.startsWith('yomiri.') || Array.isArray(rawManifest) || Array.isArray(rawManifest.sources) || Array.isArray(rawManifest.extensions))) {
        await addRepository(trimmed);
        const repoPlugin: InstalledPlugin = {
          url: trimmed,
          manifest: {
            id: 'repo.' + (rawManifest.name ? String(rawManifest.name).toLowerCase().replace(/[^a-z0-9]/g, '') : trimmed.replace(/[^a-zA-Z0-9]/g, '_')),
            name: rawManifest.name || 'Repositorio de Fuentes',
            version: '1.0.0',
            description: 'Repositorio de fuentes de manga/anime unificado.',
            types: ['manga'],
            resources: ['catalog', 'meta', 'reader'],
            catalogs: [{ type: 'manga', id: 'repo', name: 'Catálogo' }],
          },
          enabled: true,
          installedAt: Date.now(),
          lastUpdated: Date.now(),
        };
        PluginStore.save(repoPlugin);
        return { success: true, plugin: repoPlugin };
      }

      // 4. Validate manifest schema
      const validation = PluginValidator.validateManifest(rawManifest);
      if (!validation.valid || !validation.manifest) {
        return { success: false, error: validation.error || 'Manifiesto inválido' };
      }

      const installed: InstalledPlugin = {
        url: trimmed,
        manifest: validation.manifest,
        enabled: true,
        installedAt: Date.now(),
        lastUpdated: Date.now(),
      };

      PluginStore.save(installed);
      return { success: true, plugin: installed };
    } catch (err: any) {
      return { success: false, error: `Error de red al consultar el plugin: ${err?.message || err}` };
    }
  }

  /**
   * Uninstalls a plugin by ID.
   */
  public uninstallPlugin(pluginId: string): boolean {
    const plugin = PluginStore.getInstalled()[pluginId];
    if (plugin) {
      if (plugin.url === '/yomiri-sources.json' || pluginId.startsWith('repo.') || repositoryIds().includes(plugin.url)) {
        try {
          void removeRepository(plugin.url);
        } catch {
          // ignore
        }
      }
    }
    return PluginStore.remove(pluginId);
  }

  /**
   * Toggles a plugin's enabled status.
   */
  public togglePlugin(pluginId: string, enabled?: boolean): boolean {
    return PluginStore.toggleEnabled(pluginId, enabled);
  }

  /**
   * Returns all dynamic sections computed from currently enabled plugins.
   * If no plugins are enabled for a category (e.g. TV), that section is omitted.
   */
  public getDynamicSections(): DynamicSection[] {
    const enabled = PluginStore.getEnabled();
    const sectionMap = new Map<string, DynamicSection>();

    for (const plugin of enabled) {
      // Source repositories keep their own installation and catalog flow.
      if (plugin.manifest.id.startsWith('repo.') || repositoryIds().includes(plugin.url) || plugin.url === '/yomiri-sources.json') continue;
      // Determine section key
      const sectionKey = plugin.manifest.behaviorHints?.defaultSection || plugin.manifest.types[0] || 'other';
      const sectionTitle = plugin.manifest.behaviorHints?.sectionTitle || this.getDefaultTitleForSection(sectionKey);
      const sectionIcon = plugin.manifest.behaviorHints?.sectionIcon || this.getDefaultIconForSection(sectionKey);

      if (!sectionMap.has(sectionKey)) {
        sectionMap.set(sectionKey, {
          id: sectionKey,
          title: sectionTitle,
          icon: sectionIcon,
          pluginIds: [],
          catalogs: [],
        });
      }

      const sec = sectionMap.get(sectionKey)!;
      if (!sec.pluginIds.includes(plugin.manifest.id)) {
        sec.pluginIds.push(plugin.manifest.id);
      }

      for (const cat of plugin.manifest.catalogs) {
        sec.catalogs.push({
          pluginId: plugin.manifest.id,
          pluginName: plugin.manifest.name,
          catalog: cat,
        });
      }
    }

    return Array.from(sectionMap.values());
  }

  /**
   * Queries a catalog for an enabled plugin.
   */
  public async queryCatalog(
    pluginId: string,
    type: string,
    catalogId: string,
    params?: { search?: string; genre?: string; skip?: number }
  ): Promise<PluginCatalogResponse> {
    const plugins = PluginStore.getInstalled();
    const plugin = plugins[pluginId];
    if (!plugin || !plugin.enabled) {
      return { metas: [] };
    }
    try {
      return await PluginDispatcher.fetchCatalog(plugin, type, catalogId, params);
    } catch (err) {
      console.error(`[PluginManager] Error querying catalog from ${pluginId}:`, err);
      return { metas: [] };
    }
  }

  /**
   * Queries metadata for a specific item in an enabled plugin.
   */
  public async queryMeta(pluginId: string, type: string, id: string): Promise<PluginMetaResponse | null> {
    const plugins = PluginStore.getInstalled();
    const plugin = plugins[pluginId];
    if (!plugin || !plugin.enabled) return null;
    const candidates=[plugin,...PluginStore.getEnabled().filter(p=>p.manifest.id!==pluginId&&p.manifest.resources.includes('meta')&&p.manifest.types.includes(type))];
    for(const candidate of candidates){
      if(!candidate.manifest.resources.includes('meta'))continue;
      try {
        const result=await PluginDispatcher.fetchMeta(candidate,type,id);
        if(result?.meta)return result;
      } catch (err) {
        console.warn(`[PluginManager] Meta error from ${candidate.manifest.id}:`, err);
      }
    }
    return null;
  }

  /**
   * Queries streams for an item in an enabled plugin.
   * Leverages Promise.allSettled for fault isolation across plugins.
   */
  public async queryStreams(pluginId: string, type: string, id: string): Promise<PluginStreamResponse> {
    const plugins = PluginStore.getInstalled();
    const plugin = plugins[pluginId];
    if (!plugin || !plugin.enabled) return { streams: [] };
    const candidates=PluginStore.getEnabled().filter(p=>p.manifest.resources.includes('stream') && p.manifest.types.includes(type) && (!p.manifest.resourceRules || p.manifest.resourceRules.some(r=>r.name==='stream'&&(!r.types||r.types.includes(type))&&(!r.idPrefixes||r.idPrefixes.some(prefix=>id.startsWith(prefix))))));
    const settled = await Promise.allSettled(candidates.map(p=>PluginDispatcher.fetchStreams(p,type,id)));
    const streams = settled
      .filter((r): r is PromiseFulfilledResult<PluginStreamResponse> => r.status === 'fulfilled')
      .flatMap(r => r.value.streams);
    return { streams };
  }

  /**
   * Queries reader pages for a manga/comic chapter in an enabled plugin.
   */
  public async queryReader(pluginId: string, type: string, id: string, chapterId: string): Promise<PluginReaderResponse> {
    const plugins = PluginStore.getInstalled();
    const plugin = plugins[pluginId];
    if (!plugin || !plugin.enabled) return { pages: [] };
    try {
      return await PluginDispatcher.fetchReader(plugin, type, id, chapterId);
    } catch (err) {
      console.warn(`[PluginManager] Reader error from ${pluginId}:`, err);
      return { pages: [] };
    }
  }

  private getDefaultTitleForSection(key: string): string {
    switch (key.toLowerCase()) {
      case 'tv':
      case 'iptv':
        return 'TV en Vivo';
      case 'movie':
      case 'movies':
        return 'Películas';
      case 'series':
        return 'Series';
      case 'anime':
        return 'Anime';
      case 'manga':
        return 'Manga & Cómics';
      case 'audio':
        return 'Audio & Radio';
      default:
        return key.charAt(0).toUpperCase() + key.slice(1);
    }
  }

  private getDefaultIconForSection(key: string): string {
    switch (key.toLowerCase()) {
      case 'tv':
      case 'iptv':
        return 'LiveTv';
      case 'movie':
      case 'movies':
      case 'series':
        return 'Movie';
      case 'anime':
        return 'PlayCircle';
      case 'manga':
        return 'MenuBook';
      case 'audio':
        return 'Radio';
      default:
        return 'Extension';
    }
  }
}

/**
 * React hook to observe active dynamic sections reactively.
 */
export function useDynamicSections(): DynamicSection[] {
  const [sections, setSections] = useState<DynamicSection[]>(() =>
    PluginManager.getInstance().getDynamicSections()
  );

  useEffect(() => {
    const update = () => {
      setSections(PluginManager.getInstance().getDynamicSections());
    };

    window.addEventListener(EVENT_PLUGINS_CHANGED, update);
    return () => window.removeEventListener(EVENT_PLUGINS_CHANGED, update);
  }, []);

  return sections;
}

/**
 * React hook to observe all installed plugins reactively.
 */
export function useInstalledPlugins(): {
  plugins: Record<string, InstalledPlugin>;
  installPlugin: (url: string) => Promise<{ success: boolean; plugin?: InstalledPlugin; error?: string }>;
  uninstallPlugin: (id: string) => boolean;
  togglePlugin: (id: string, enabled?: boolean) => boolean;
} {
  const filterExternalPlugins = () => {
    const raw = PluginStore.getInstalled();
    const repos = new Set(repositoryIds());
    const filtered: Record<string, InstalledPlugin> = {};
    for (const [id, plugin] of Object.entries(raw)) {
      if (id.startsWith('repo.') || repos.has(plugin.url) || plugin.url === '/yomiri-sources.json') continue;
      filtered[id] = plugin;
    }
    return filtered;
  };

  const [plugins, setPlugins] = useState<Record<string, InstalledPlugin>>(filterExternalPlugins);

  useEffect(() => {
    const update = () => setPlugins(filterExternalPlugins());
    window.addEventListener(EVENT_PLUGINS_CHANGED, update);
    window.addEventListener('yomiri-sources-changed', update);
    return () => {
      window.removeEventListener(EVENT_PLUGINS_CHANGED, update);
      window.removeEventListener('yomiri-sources-changed', update);
    };
  }, []);

  const manager = PluginManager.getInstance();

  return {
    plugins,
    installPlugin: manager.installPlugin.bind(manager),
    uninstallPlugin: manager.uninstallPlugin.bind(manager),
    togglePlugin: manager.togglePlugin.bind(manager),
  };
}
